import React, { FC, useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  TextField,
} from '@mui/material';

/** Mirrors REPORT_REASONS in server/models/report.ts — the server validates. */
export const REPORT_REASONS: { value: string; label: string }[] = [
  { value: 'spam', label: 'Spam or scam' },
  { value: 'harassment', label: 'Harassment or bullying' },
  { value: 'hate', label: 'Hate speech' },
  { value: 'violence', label: 'Violence or threats' },
  { value: 'sexual_content', label: 'Sexual content' },
  { value: 'child_safety', label: 'Child safety (sexual content or harm involving a minor)' },
  { value: 'misinformation', label: 'Misinformation' },
  { value: 'other', label: 'Something else' },
];

interface ReportDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (reason: string, detail?: string) => Promise<void> | void;
  /** Defaults are the post copy; comments and profiles pass their own. */
  title?: string;
  description?: string;
}

/**
 * Reason picker for reporting a post. Fixed reasons rather than free text so
 * a future moderation queue can group and count them; 'Something else' opens
 * the detail field for anything the list doesn't cover.
 */
const ReportDialog: FC<ReportDialogProps> = ({
  open,
  onClose,
  onSubmit,
  title = 'Report this post',
  description = "Tell us what's wrong. We'll hide the post from your feeds either way.",
}) => {
  const [reason, setReason] = useState('spam');
  const [detail, setDetail] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleClose = () => {
    if (submitting) return;
    onClose();
    // Reset for the next post, so a previous choice never leaks into it.
    setReason('spam');
    setDetail('');
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      await onSubmit(reason, detail.trim() || undefined);
      onClose();
      setReason('spam');
      setDetail('');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="xs">
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 1.5, fontSize: '14px' }}>
          {description}
        </DialogContentText>
        <FormControl>
          <RadioGroup value={reason} onChange={(e) => setReason(e.target.value)}>
            {REPORT_REASONS.map((r) => (
              <FormControlLabel
                key={r.value}
                value={r.value}
                control={<Radio size="small" />}
                label={r.label}
              />
            ))}
          </RadioGroup>
        </FormControl>
        {reason === 'other' && (
          <TextField
            fullWidth
            multiline
            minRows={2}
            size="small"
            placeholder="What happened?"
            value={detail}
            onChange={(e) => setDetail(e.target.value.slice(0, 500))}
            sx={{ mt: 1 }}
          />
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={submitting}>
          Cancel
        </Button>
        <Button onClick={handleSubmit} color="primary" variant="contained" disabled={submitting}>
          {submitting ? 'Reporting…' : 'Report'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ReportDialog;
